function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import { clearStorage, Navigation, Page, Storage, setWWNDS } from 'interactive';
import ComponentLibrary from './ComponentLibrary';
import { deepMap, void_tags } from './DataStructures';
import ErrorBoundary from './ErrorBoundary';

// Set to true to debug this file
const DEBUG = false;
let renderIteration = 0;

// Input: In-memory DataStructure format. Output: React components.
export default class InteractiveRenderer extends React.Component {
  constructor(props) {
    super(props);
    this.renderChild = this.renderChild.bind(this);
    this.images = new Set();
    deepMap(props.data, (e, key) => {
      if (!e) {
        return e;
      }
      if (typeof e === "string" && e.startsWith("http")) {
        this.images.add(e);
      } else if (e.tagName === "img") {
        this.images.add(e.props.src);
      } else if (e.tagName === "Figure") {
        this.images.add(e.props.src);
      } else if (["TaskImageClick", "TaskCallout", "TaskDragDrop"].includes(e.tagName)) {
        this.images.add(e.props.picture);
      } else if (e.tagName === "TaskCustom") {
        let matches = [...e.props.inner.matchAll(/<img [^>]*src=["']([^"']+)[^>]*/g)];
        for (let [match, capture] of matches) {
          this.images.add(capture);
        }
      }
      return e;
    });
  }
  renderChild(child, key, replace_children) {
    //DEBUG && console.log("InteractiveRenderer renderChild", child, key, replace_children)
    if (child) {
      if (child.type === "Interactive") {
        return /*#__PURE__*/React.createElement("div", this.props.attributes, /*#__PURE__*/React.createElement("div", {
          className: "sr-only",
          "aria-hidden": "true"
        }, [...this.images].map(src => /*#__PURE__*/React.createElement("img", {
          alt: "",
          src: src
        }))), /*#__PURE__*/React.createElement(Navigation, _extends({
          key: key
        }, this.props.attributes, {
          onReset: () => {
            DEBUG && console.log("navigation onReset InteractiveRenderer");
            clearStorage();
          }
        }), replace_children ? this.props.children : child.panels));
      } else if (child.type === "Panel") {
        return /*#__PURE__*/React.createElement(Page, _extends({
          key: key
        }, this.props.attributes), replace_children ? this.props.children : child.children);
      } else if (child.type === "Element") {
        if (void_tags.includes(child.tagName) || child.children === undefined) {
          let {
            children,
            ...rest
          } = child.props;
          return /*#__PURE__*/React.createElement(child.tagName || "mark", {
            key: key,
            ...this.props.attributes,
            ...rest
          });
        } else {
          return /*#__PURE__*/React.createElement(child.tagName || "mark", {
            key: key,
            ...this.props.attributes,
            ...child.props
          }, ...(replace_children ? this.props.children : child.children));
        }
      } else if (child.type === "Component") {
        let tag = child.tagName;
        let dot = tag.indexOf(".");
        let tag2;
        if (dot !== -1) {
          tag2 = tag.substr(dot + 1);
          tag = tag.substr(0, dot);
        }
        let Component = ComponentLibrary[tag];
        if (Component && (tag2 ? Component[tag2] : true)) {
          if (tag2) {
            Component = Component[tag2];
          }
          ++this.renderKey;
          //return React.createElement(Component, {key: key, state: Storage(renderIteration + "-" + this.renderKey), ...child.props}, ...child.children)
          let key_comination = (this.props.storageKey || renderIteration) + "-" + this.renderKey + key + child.tagName;
          let storage = Storage(key_comination);
          if (storage.hash === undefined) {
            storage.hash = [];
          }
          storage.hash.push(key_comination);
          if (void_tags.includes(child.tagName)) {
            return /*#__PURE__*/React.createElement(Component, _extends({
              key: key,
              state: storage
            }, this.props.attributes, child.props));
          } else {
            return /*#__PURE__*/React.createElement(Component, _extends({
              key: key,
              state: storage
            }, this.props.attributes, child.props), replace_children ? this.props.children : child.children);
          }
        } else {
          return /*#__PURE__*/React.createElement("mark", {
            key: key
          }, "Invalid component \"", child.tagName, "\"");
        }
      }
    }
    if (child.hasOwnProperty("text") && !key.endsWith('props')) {
      return child.text;
    }
    return child;
  }
  // Helper function so we don't get confused by arrays.
  renderChildren(children) {
    if (children.length === 1 && this.props.children) {
      DEBUG && console.log("InteractiveRenderer renderChildren replacing");
      return this.renderChild(children[0], 0, true);
    }
    return deepMap(children, this.renderChild);
  }
  render() {
    var _this$props$forceWWND, _this$props$data, _this$props$data4;
    this.renderKey = 0;
    let styleAdd = this.props.style || {};
    let wwnds = (_this$props$forceWWND = this.props.forceWWNDS) !== null && _this$props$forceWWND !== void 0 ? _this$props$forceWWND : (_this$props$data = this.props.data) === null || _this$props$data === void 0 ? void 0 : _this$props$data.wwnds;
    if (wwnds !== undefined) {
      setWWNDS(wwnds);
    }
    if (this.props.fullWidth) {
      var _this$props$data2, _this$props$data3;
      return /*#__PURE__*/React.createElement("div", {
        className: "interactive-renderer",
        style: {
          maxWidth: '728px',
          margin: 'auto',
          ...styleAdd
        }
      }, /*#__PURE__*/React.createElement("style", null, (_this$props$data2 = this.props.data) === null || _this$props$data2 === void 0 ? void 0 : _this$props$data2.css), /*#__PURE__*/React.createElement(ErrorBoundary, {
        key: this.renderKey + "-" + renderIteration
      }, this.renderChildren((_this$props$data3 = this.props.data) !== null && _this$props$data3 !== void 0 && _this$props$data3.map ? this.props.data : [this.props.data])));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, this.props.toolbar && /*#__PURE__*/React.createElement("div", {
      class: "sr-only",
      "aria-hidden": "true",
      tabindex: 0,
      onFocus: this.props.toolbarOut
    }), /*#__PURE__*/React.createElement(ErrorBoundary, {
      key: this.renderKey + "-" + renderIteration
    }, this.renderChildren((_this$props$data4 = this.props.data) !== null && _this$props$data4 !== void 0 && _this$props$data4.map ? this.props.data : [this.props.data])));
  }
}